import { useState } from 'react'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { ChevronUp, ChevronDown, ChevronsUpDown, LineChart } from 'lucide-react'
import { AlertBanner } from '../components/ui/AlertBanner'
import { KPICard } from '../components/ui/KPICard'
import { RecurringRevenueChart } from '../components/dashboard/RecurringRevenueChart'
import { useRecurringRevenueKPIs } from '../hooks/useKPIs'
import { useGlobalFilters } from '../contexts/GlobalFiltersContext'
import { useBUContext } from '../contexts/BUContext'

function formatEur(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatEurK(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2).replace('.', ',')} M €`
  if (value >= 1_000) return `${Math.round(value / 1_000).toLocaleString('it-IT')}k €`
  return formatEur(value)
}

function PanelHeader({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
      <div className="flex items-center gap-2">
        <div className="h-3.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
        <p className="section-label">{label}</p>
      </div>
      {children}
    </div>
  )
}

const CONFIDENCE_CONFIG = {
  high:   { label: 'Alta',   dot: 'bg-emerald-400', badge: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/20' },
  medium: { label: 'Media',  dot: 'bg-amber-400',   badge: 'bg-amber-500/10 text-amber-400 ring-amber-500/20'     },
  low:    { label: 'Bassa',  dot: 'bg-red-400',     badge: 'bg-red-500/10 text-red-400 ring-red-500/20'           },
}

function ConfidenceChip({ level }: { level: 'high' | 'medium' | 'low' }) {
  const cfg = CONFIDENCE_CONFIG[level]
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${cfg.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  )
}

const SL_COLORS = [
  'bg-gradient-to-r from-indigo-600 to-indigo-400',
  'bg-gradient-to-r from-purple-600 to-purple-400',
  'bg-gradient-to-r from-cyan-600 to-cyan-400',
  'bg-gradient-to-r from-amber-600 to-amber-400',
  'bg-gradient-to-r from-emerald-600 to-emerald-400',
]

type SortKey = 'days_to_expiry' | 'forecast_total' | 'monthly_value' | 'confidence'
type SortDir = 'asc' | 'desc'

const CONFIDENCE_ORDER = { high: 0, medium: 1, low: 2 }

function SortIcon({ k, sortKey, sortDir }: { k: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (k !== sortKey) return <ChevronsUpDown className="h-3 w-3 opacity-30" />
  return sortDir === 'asc'
    ? <ChevronUp className="h-3 w-3 text-indigo-400" />
    : <ChevronDown className="h-3 w-3 text-indigo-400" />
}

export function RecurringRevenue() {
  const { filters } = useGlobalFilters()
  const { selectedBuId } = useBUContext()
  const { data: kpis, isLoading, error } = useRecurringRevenueKPIs({
    customer: filters.customer,
    service_line: filters.service_line,
    buId: selectedBuId,
  })
  const [sortKey, setSortKey] = useState<SortKey>('days_to_expiry')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  function handleSort(key: SortKey) {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('asc') }
  }

  const sortedContracts = kpis
    ? [...kpis.contracts].sort((a, b) => {
        let diff = 0
        if (sortKey === 'days_to_expiry') {
          diff = (a.days_to_expiry ?? 99999) - (b.days_to_expiry ?? 99999)
        } else if (sortKey === 'forecast_total') {
          diff = a.forecast_total - b.forecast_total
        } else if (sortKey === 'monthly_value') {
          diff = a.monthly_value - b.monthly_value
        } else if (sortKey === 'confidence') {
          diff = CONFIDENCE_ORDER[a.confidence] - CONFIDENCE_ORDER[b.confidence]
        }
        return sortDir === 'asc' ? diff : -diff
      })
    : []

  const slTotal = kpis?.by_service_line.reduce((acc, sl) => acc + sl.forecast_total, 0) ?? 0

  const subtitle = kpis
    ? `Aggiornato al ${format(new Date(kpis.computed_at), "d MMM yyyy 'ore' HH:mm", { locale: it })}`
    : format(new Date(), 'd MMM yyyy', { locale: it })

  // Badge scadenze in topbar del PageHeader
  const expiryBadge = kpis && (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${
      kpis.contracts_expiring_90d === 0
        ? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/20'
        : kpis.contracts_expiring_30d === 0
          ? 'bg-amber-500/10 text-amber-400 ring-amber-500/20'
          : 'bg-red-500/10 text-red-400 ring-red-500/20'
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${
        kpis.contracts_expiring_90d === 0 ? 'bg-emerald-400'
          : kpis.contracts_expiring_30d === 0 ? 'bg-amber-400' : 'bg-red-400'
      }`} />
      {kpis.contracts_expiring_30d > 0
        ? `${kpis.contracts_expiring_30d} contratti in scadenza entro 30gg`
        : kpis.contracts_expiring_90d > 0
          ? `${kpis.contracts_expiring_90d} contratti in scadenza entro 90gg`
          : 'Nessuna scadenza imminente'}
    </span>
  )

  return (
    <section aria-label="Recurring Revenue" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 bg-gradient-to-br from-violet-500 to-indigo-600"
            style={{ boxShadow: '0 0 24px rgba(139,92,246,0.35), 0 4px 16px rgba(0,0,0,0.4)' }}>
            <LineChart size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[--km-text-primary] tracking-tight leading-tight">
              Recurring Revenue
            </h1>
            <p className="text-sm text-[--km-text-muted] mt-1">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {(kpis?.growth_rate_applied ?? 0) > 0 && (
            <span className="text-xs text-[--km-text-muted] font-medium">
              Tasso crescita applicato: {((kpis?.growth_rate_applied ?? 0) * 100).toFixed(1)}%
            </span>
          )}
          {expiryBadge}
        </div>
      </div>

      {error && (
        <AlertBanner severity="critical" message="Impossibile caricare il forecast ricavi ricorrenti. Riprovare." />
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="stagger-1">
          <KPICard
            label="Forecast residuo"
            value={kpis ? formatEur(kpis.forecast_total) : '—'}
            status="neutral"
            loading={isLoading}
            subtext={kpis ? `${kpis.active_contracts} contratti attivi` : undefined}
          />
        </div>
        <div className="stagger-2">
          <KPICard
            label="Anno 1"
            value={kpis ? formatEur(kpis.forecast_year_1) : '—'}
            status="neutral"
            loading={isLoading}
            subtext="Prossimi 12 mesi"
          />
        </div>
        <div className="stagger-3">
          <KPICard
            label="Anno 2"
            value={kpis ? formatEur(kpis.forecast_year_2) : '—'}
            status="neutral"
            loading={isLoading}
            subtext="Mesi 13–24"
          />
        </div>
        <div className="stagger-4">
          <KPICard
            label="Media mensile"
            value={kpis ? formatEur(kpis.monthly_avg) : '—'}
            status="neutral"
            loading={isLoading}
            subtext="Revenue mensile media"
          />
        </div>
      </div>

      {/* Quarterly chart + Confidence + Service lines */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Quarterly chart — 2/3 */}
        <div className="lg:col-span-2 rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
          <PanelHeader label="Forecast ricavi ricorrenti — waterfall trimestrale" />
          <div className="h-60">
            <RecurringRevenueChart data={kpis?.quarterly ?? []} />
          </div>
        </div>

        {/* Right column — confidence + service lines — 1/3 */}
        <div className="lg:col-span-1 space-y-4">
          {/* Confidence breakdown */}
          <div className="rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-5 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
            <PanelHeader label="Confidence scoring" />
            {isLoading ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3].map(i => <div key={i} className="h-8 rounded-xl bg-[--km-surface-3]" />)}
              </div>
            ) : kpis ? (
              <div className="space-y-3">
                {(['high', 'medium', 'low'] as const).map(level => {
                  const entry = kpis.confidence_breakdown[level]
                  if (!entry) return null
                  return (
                    <div key={level} className="flex items-center justify-between gap-3 p-2.5 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                      <ConfidenceChip level={level} />
                      <div className="text-right">
                        <p className="text-sm font-semibold text-[--km-text-primary]">
                          {formatEurK(entry.amount)}
                        </p>
                        <p className="text-xs text-[--km-text-muted]">
                          {entry.count} {entry.count === 1 ? 'contratto' : 'contratti'}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>

          {/* By service line */}
          {kpis && kpis.by_service_line.length > 0 && (
            <div className="rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-5 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
              <PanelHeader label="Per service line" />
              <div className="space-y-3.5">
                {kpis.by_service_line.slice(0, 5).map((sl, idx) => {
                  const pct = slTotal > 0 ? (sl.forecast_total / slTotal) * 100 : 0
                  return (
                    <div key={sl.service_line}>
                      <div className="mb-1.5 flex items-center justify-between text-xs">
                        <span className="font-semibold text-[--km-text-secondary] truncate max-w-[140px]">
                          {sl.service_line}
                        </span>
                        <span className="text-[--km-text-muted] font-medium ml-2">
                          {pct.toFixed(0)}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-[#182038] overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${SL_COLORS[idx % SL_COLORS.length]}`}
                          style={{ width: `${Math.max(pct, pct > 0 ? 2 : 0)}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contracts table */}
      <div className="rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm shadow-[0_4px_24px_rgba(0,0,0,0.4)] overflow-hidden">
        <div className="px-6 py-4 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-3.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
            <p className="section-label">Dettaglio contratti ricorrenti</p>
          </div>
          {kpis && (
            <span className="text-xs text-[--km-text-muted] font-medium">
              {kpis.active_contracts} attivi · {kpis.contracts_expiring_365d} in scadenza entro 12 mesi
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="p-6 space-y-3 animate-pulse">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-10 rounded-xl bg-[--km-surface-3]" />)}
          </div>
        ) : sortedContracts.length === 0 ? (
          <div
            data-testid="recurring-contracts-empty"
            className="flex flex-col items-center justify-center py-14 text-center"
          >
            <p className="text-sm font-semibold text-[--km-text-secondary]">Nessun contratto ricorrente</p>
            <p className="text-xs text-[--km-text-muted] mt-1">Carica i contratti ricorrenti per attivare il forecast</p>
          </div>
        ) : (
          <div data-testid="recurring-contracts-table">
            {/* Mobile card view (< sm) */}
            <div className="sm:hidden divide-y divide-white/[0.04]">
              {sortedContracts.map(c => (
                <div key={c.internal_id} className="px-4 py-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-[--km-text-primary] text-sm leading-tight truncate">{c.customer_name}</p>
                      <p className="text-xs text-[--km-text-muted] mt-0.5">{c.service_line || '—'} · {c.period_label}</p>
                    </div>
                    <ConfidenceChip level={c.confidence} />
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <p className="text-[--km-text-muted] mb-0.5">Mensile</p>
                      <p className="text-[--km-text-secondary] tabular-nums font-medium">{formatEur(c.monthly_value)}</p>
                    </div>
                    <div>
                      <p className="text-[--km-text-muted] mb-0.5">Forecast totale</p>
                      <p className="text-[--km-text-primary] tabular-nums font-semibold">{formatEur(c.forecast_total)}</p>
                    </div>
                    <div>
                      <p className="text-[--km-text-muted] mb-0.5">Mesi residui</p>
                      <p className="text-[--km-text-secondary] tabular-nums">{c.residual_months}</p>
                    </div>
                    <div>
                      <p className="text-[--km-text-muted] mb-0.5">Scadenza</p>
                      {c.days_to_expiry != null ? (
                        <p className={`tabular-nums font-medium ${
                          c.days_to_expiry <= 30 ? 'text-red-400'
                            : c.days_to_expiry <= 90 ? 'text-amber-400'
                              : 'text-[--km-text-muted]'
                        }`}>
                          {c.days_to_expiry <= 0 ? 'Scaduto' : `${c.days_to_expiry}gg`}
                        </p>
                      ) : (
                        <p className="text-[--km-text-muted]">Auto-rinnovo</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table view (>= sm) */}
            <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/[0.04]">
                  {[
                    { label: 'Cliente',      align: 'left',  sort: null },
                    { label: 'Service line', align: 'left',  sort: null },
                    { label: 'Periodicità',  align: 'left',  sort: null },
                    { label: 'Mensile',      align: 'right', sort: 'monthly_value' as SortKey },
                    { label: 'Mesi res.',    align: 'right', sort: null },
                    { label: 'Forecast',     align: 'right', sort: 'forecast_total' as SortKey },
                    { label: 'Scadenza',     align: 'right', sort: 'days_to_expiry' as SortKey },
                    { label: 'Conf.',        align: 'right', sort: 'confidence' as SortKey },
                  ].map(col => (
                    <th
                      key={col.label}
                      className={`px-4 py-3 font-semibold text-[--km-text-muted] ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.sort ? 'cursor-pointer hover:text-[--km-text-secondary] transition-colors' : ''}`}
                      onClick={col.sort ? () => handleSort(col.sort as SortKey) : undefined}
                    >
                      {col.sort ? (
                        <span className="inline-flex items-center gap-1 justify-end">
                          {col.label}
                          <SortIcon k={col.sort as SortKey} sortKey={sortKey} sortDir={sortDir} />
                        </span>
                      ) : col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedContracts.map(c => (
                  <tr
                    key={c.internal_id}
                    className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-4 py-3 font-semibold text-[--km-text-primary] max-w-[140px] truncate">
                      {c.customer_name}
                    </td>
                    <td className="px-4 py-3 text-[--km-text-muted]">{c.service_line || '—'}</td>
                    <td className="px-4 py-3 text-[--km-text-muted]">{c.period_label}</td>
                    <td className="px-4 py-3 text-right text-[--km-text-secondary] font-medium">
                      {formatEur(c.monthly_value)}
                    </td>
                    <td className="px-4 py-3 text-right text-[--km-text-muted]">{c.residual_months}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[--km-text-primary]">
                      {formatEur(c.forecast_total)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {c.days_to_expiry != null ? (
                        <span className={
                          c.days_to_expiry <= 30 ? 'text-red-400 font-semibold'
                            : c.days_to_expiry <= 90 ? 'text-amber-400 font-medium'
                              : 'text-[--km-text-muted]'
                        }>
                          {c.days_to_expiry <= 0 ? 'Scaduto' : `${c.days_to_expiry}gg`}
                        </span>
                      ) : (
                        <span className="text-[--km-text-muted]">Auto-rinnovo</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ConfidenceChip level={c.confidence} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
